import type { HitTargets, Node, Relationship } from '@neo4j-nvl/base';
import type { MouseEventCallbacks } from '@neo4j-nvl/react';
import { xor } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';

import { isMac } from '../utils';
import type { GraphSelection, InteractionMode } from './graph-viz-context';

const GESTURES = ['single', 'box', 'lasso'] as const;
export type Gesture = (typeof GESTURES)[number];

const isMultiSelectKeyPressed = (evt: MouseEvent): boolean => (!isMac && evt.ctrlKey) || (isMac && evt.metaKey);

const isClickingOnTextInput = (e: MouseEvent): boolean => {
  if (!(e.target instanceof HTMLElement)) {
    return false;
  }
  return e.target.isContentEditable || ['INPUT', 'TEXTAREA'].includes(e.target.tagName);
};

export type ManagedSelectionArgs = {
  selected: GraphSelection;
  setSelected: (newSelection: GraphSelection) => void;
  gesture: Gesture;
  interactionMode: InteractionMode;
  setInteractionMode: (newInteractionMode: InteractionMode) => void;
  mouseEventCallbacks: MouseEventCallbacks;
  nvlGraph: {
    nodes: Node[];
    rels: Relationship[];
  };
};

export function useManagedSelectionState({
  selected,
  setSelected,
  gesture,
  interactionMode,
  setInteractionMode,
  mouseEventCallbacks,
  nvlGraph,
}: ManagedSelectionArgs) {
  const panOnSpace = useCallback(
    (evt: KeyboardEvent) => {
      // We could check the gesture here
      // but it doesn't really matter if we switch to panning mode even in single select
      if (interactionMode === 'select' && evt.key === ' ') {
        setInteractionMode('pan');
      }
    },
    [interactionMode, setInteractionMode],
  );

  const stopPanningReleaseSpace = useCallback(
    (evt: KeyboardEvent) => {
      if (interactionMode === 'pan' && evt.key === ' ') {
        setInteractionMode('select');
      }
    },
    [interactionMode, setInteractionMode],
  );

  useEffect(() => {
    document.addEventListener('keydown', panOnSpace);
    document.addEventListener('keyup', stopPanningReleaseSpace);
    return () => {
      document.removeEventListener('keydown', panOnSpace);
      document.removeEventListener('keyup', stopPanningReleaseSpace);
    };
  }, [panOnSpace, stopPanningReleaseSpace]);

  const {
    onBoxSelect: originalBoxSelect,
    onLassoSelect: originalOnLassoSelect,
    onLassoStarted,
    onBoxStarted,
    onPan = true,
    onHover,
    onHoverNodeMargin,
    onNodeClick: originalOnNodeClick,
    onRelationshipClick: originalOnRelationshipClick,
    onDragStart: originalOnDragStart,
    onDragEnd: originalOnDragEnd,
    onDrawEnded: originalOnDrawEnded,
    onDrawStarted: originalOnDrawStarted,
    onCanvasClick: originalOnCanvasClick,
    onNodeDoubleClick: originalOnNodeDoubleClick,
    onRelationshipDoubleClick: originalOnRelationshipDoubleClick,
  } = mouseEventCallbacks;

  const onCanvasClick = useCallback(
    (evt: MouseEvent) => {
      if (isClickingOnTextInput(evt)) {
        return;
      }

      // Clear selection
      setSelected({ nodeIds: [], relationshipIds: [] });

      if (typeof originalOnCanvasClick === 'function') {
        originalOnCanvasClick(evt);
      }
    },
    [originalOnCanvasClick, setSelected],
  );

  const onDragStart = useCallback(
    (draggedNodes: Node[], evt: MouseEvent) => {
      setInteractionMode('drag');

      // Update Selection
      const draggedNodeIds = draggedNodes.map((n) => n.id);
      if (selected.nodeIds.length === 0 || isMultiSelectKeyPressed(evt)) {
        setSelected({ nodeIds: draggedNodeIds, relationshipIds: selected.relationshipIds });
        return;
      }
      setSelected({ nodeIds: draggedNodeIds, relationshipIds: selected.relationshipIds });

      if (typeof originalOnDragStart === 'function') {
        originalOnDragStart(draggedNodes, evt);
      }
    },
    [setSelected, originalOnDragStart, selected, setInteractionMode],
  );

  const onDragEnd = useCallback(
    (draggedNodes: Node[], evt: MouseEvent) => {
      if (typeof originalOnDragEnd === 'function') {
        originalOnDragEnd(draggedNodes, evt);
      }
      setInteractionMode('select');
    },
    [originalOnDragEnd, setInteractionMode],
  );

  // wrapping so that there's a function passed, even if the original function is undefined
  const onDrawStarted = useCallback(
    (evt: MouseEvent) => {
      if (typeof originalOnDrawStarted === 'function') {
        originalOnDrawStarted(evt);
      }
    },
    [originalOnDrawStarted],
  );

  const onDrawEnded = useCallback(
    (newRelationshipToAdd: Relationship | null, newTargetNodeToAdd: Node | null, event: MouseEvent) => {
      if (typeof originalOnDrawEnded === 'function') {
        originalOnDrawEnded(newRelationshipToAdd, newTargetNodeToAdd, event);
      }
    },
    [originalOnDrawEnded],
  );

  const onNodeClick = useCallback(
    (node: Node, hitElements: HitTargets, evt: MouseEvent) => {
      if (isClickingOnTextInput(evt)) {
        return;
      }

      if (isMultiSelectKeyPressed(evt)) {
        // if not selected, add to selection, otherwise deselect
        const nodeIsSelected = selected.nodeIds.includes(node.id);
        if (nodeIsSelected) {
          const newSelectedNodeIds = selected.nodeIds.filter((id) => id !== node.id);
          setSelected({ nodeIds: newSelectedNodeIds, relationshipIds: selected.relationshipIds });
        } else {
          const newSelectedNodeIds = [...selected.nodeIds, node.id];
          setSelected({ nodeIds: newSelectedNodeIds, relationshipIds: selected.relationshipIds });
        }
      } else {
        setSelected({ nodeIds: [node.id], relationshipIds: [] });
      }
      if (typeof originalOnNodeClick === 'function') {
        originalOnNodeClick(node, hitElements, evt);
      }
    },
    [setSelected, selected, originalOnNodeClick],
  );

  const onRelationshipClick = useCallback(
    (relationship: Relationship, hitElements: HitTargets, evt: MouseEvent) => {
      if (isClickingOnTextInput(evt)) {
        return;
      }

      if (isMultiSelectKeyPressed(evt)) {
        // if not selected, add to selection, otherwise deselect
        const relationshipIsSelected = selected.relationshipIds.includes(relationship.id);
        if (relationshipIsSelected) {
          const newSelectedRelIds = selected.relationshipIds.filter((id) => id !== relationship.id);
          setSelected({ nodeIds: selected.nodeIds, relationshipIds: newSelectedRelIds });
        } else {
          const newSelectedRelIds = [...selected.relationshipIds, relationship.id];
          setSelected({ nodeIds: selected.nodeIds, relationshipIds: newSelectedRelIds });
        }
      } else {
        setSelected({ nodeIds: [], relationshipIds: [relationship.id] });
      }
      if (typeof originalOnRelationshipClick === 'function') {
        originalOnRelationshipClick(relationship, hitElements, evt);
      }
    },
    [setSelected, selected, originalOnRelationshipClick],
  );

  const onNodeDoubleClick = useCallback(
    (node: Node, hitElements: HitTargets, evt: MouseEvent) => {
      if (isClickingOnTextInput(evt)) {
        return;
      }
      if (typeof originalOnNodeDoubleClick === 'function') {
        originalOnNodeDoubleClick(node, hitElements, evt);
      }
    },
    [originalOnNodeDoubleClick],
  );

  const onRelationshipDoubleClick = useCallback(
    (relationship: Relationship, hitElements: HitTargets, evt: MouseEvent) => {
      if (isClickingOnTextInput(evt)) {
        return;
      }
      if (typeof originalOnRelationshipDoubleClick === 'function') {
        originalOnRelationshipDoubleClick(relationship, hitElements, evt);
      }
    },
    [originalOnRelationshipDoubleClick],
  );

  const onMultiSelect = useCallback(
    (nodes: Node[], rels: Relationship[], evt: MouseEvent) => {
      const nodeIds = nodes.map((n) => n.id);
      const relationshipIds = rels.map((r) => r.id);
      if (isMultiSelectKeyPressed(evt)) {
        // Toggle selection: items that are already selected should be removed from selection,
        // those that are not should be added
        const currentlySelectedNodeIds = selected.nodeIds;
        const currentlySelectedRelIds = selected.relationshipIds;

        const newSelectedNodeIds = xor(currentlySelectedNodeIds, nodeIds);
        const newSelectedRelIds = xor(currentlySelectedRelIds, relationshipIds);

        setSelected({ nodeIds: newSelectedNodeIds, relationshipIds: newSelectedRelIds });
      } else {
        // REPLACE selection
        setSelected({ nodeIds: nodeIds, relationshipIds: relationshipIds });
      }
    },
    [setSelected, selected],
  );

  const onLassoSelect = useCallback(
    ({ nodes, rels }: { nodes: Node[]; rels: Relationship[] }, evt: MouseEvent) => {
      onMultiSelect(nodes, rels, evt);
      if (typeof originalOnLassoSelect === 'function') {
        originalOnLassoSelect({ nodes, rels }, evt);
      }
    },
    [onMultiSelect, originalOnLassoSelect],
  );

  const onBoxSelect = useCallback(
    ({ nodes, rels }: { nodes: Node[]; rels: Relationship[] }, evt: MouseEvent) => {
      onMultiSelect(nodes, rels, evt);
      if (typeof originalBoxSelect === 'function') {
        originalBoxSelect({ nodes, rels }, evt);
      }
    },
    [onMultiSelect, originalBoxSelect],
  );

  const inDrawMode = interactionMode === 'draw';

  const inSelectMode = interactionMode === 'select';
  const enableBox = inSelectMode && gesture === 'box';
  const enableLasso = inSelectMode && gesture === 'lasso';

  const enabledPan = interactionMode === 'pan' || (inSelectMode && gesture === 'single');

  const canDrag = interactionMode === 'drag' || interactionMode === 'select';

  const wrappedMouseEventCallbacks = useMemo(
    () => ({
      ...mouseEventCallbacks,
      onZoom: mouseEventCallbacks.onZoom ?? true,
      onNodeDoubleClick: inSelectMode ? onNodeDoubleClick : false,
      onRelationshipDoubleClick: inSelectMode ? onRelationshipDoubleClick : false,
      onNodeClick: inSelectMode ? onNodeClick : false,
      onRelationshipClick: inSelectMode ? onRelationshipClick : false,
      onBoxSelect: enableBox ? onBoxSelect : false,
      onLassoSelect: enableLasso ? onLassoSelect : false,
      onBoxStarted: enableBox ? onBoxStarted : false,
      onLassoStarted: enableLasso ? onLassoStarted : false,
      onPan: enabledPan ? onPan : false,
      onHover: inSelectMode ? onHover : false,
      onDragStart: canDrag ? onDragStart : false,
      onDragEnd: canDrag ? onDragEnd : false,
      onDrawEnded: inDrawMode ? onDrawEnded : false,
      onDrawStarted: inDrawMode ? onDrawStarted : false,
      onHoverNodeMargin: inDrawMode ? onHoverNodeMargin : false,
      onCanvasClick: inSelectMode ? onCanvasClick : false,
    }),
    [
      canDrag,
      enableBox,
      enableLasso,
      enabledPan,
      inDrawMode,
      inSelectMode,
      mouseEventCallbacks,
      onBoxSelect,
      onBoxStarted,
      onCanvasClick,
      onDragEnd,
      onDragStart,
      onDrawEnded,
      onDrawStarted,
      onHover,
      onHoverNodeMargin,
      onLassoSelect,
      onLassoStarted,
      onNodeClick,
      onNodeDoubleClick,
      onPan,
      onRelationshipClick,
      onRelationshipDoubleClick,
    ],
  );

  const selectionSet = useMemo(
    () => ({ nodeIds: new Set(selected.nodeIds), relIds: new Set(selected.relationshipIds) }),
    [selected],
  );
  const nodesWithSelection = useMemo(
    () => nvlGraph.nodes.map((n) => ({ ...n, selected: selectionSet.nodeIds.has(n.id) })),
    [nvlGraph.nodes, selectionSet],
  );

  const relsWithSelection = useMemo(
    () => nvlGraph.rels.map((r) => ({ ...r, selected: selectionSet.relIds.has(r.id) })),
    [nvlGraph.rels, selectionSet],
  );

  return { relsWithSelection, nodesWithSelection, wrappedMouseEventCallbacks };
}
